import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import useStepper from 'hooks/useStepper';
import type { StepInitStatus, StepType } from 'components/layout';
import { FormNavigation, StepperPaper } from 'components/layout';
import type { Resolver } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import AdAccountsForm from './AdAccountsForm';
import DeepLinkInitializer from './DeepLinkInitializer';
import CampaignsForm from './CampaignsForm';
import AdSetsForm from './AdSetsForm';
import CreativesForm from './CreativesForm';
import MarketingProcessTabs from './MarketingProcessTabs';
import AdForm from './AdForm';
import LaunchPreviewForm from './LaunchPreviewForm';
import useJobStatusCheck from 'hooks/useJobStatusCheck';
import type {
  CreateFacebookAdFromPacksParams,
  CreateFacebookAdResponse,
  CreatePartnershipAdsParams,
  CreateScalingEgAdsParams,
} from '../api';
import {
  useCampaigns,
  useCreateFacebookAd,
  useCreatePartnershipAds,
  useCreateScalingEgAds,
} from '../api';
import queryKeys from '../api/queryKeys';
import {
  AdCreationProgress,
  AddPackButton,
  CircularProgress,
  ConfirmationDialog,
} from 'components/common';
import AddHypothesisPackButton from './AddHypothesisPackButton';
import CreateMixedPackButton from './CreateMixedPackButton';
import SplitInPacksButton from './SplitInPacksButton';
import { JobResultTable } from './FacebookFormComponents';
import { queryClient } from 'lib/react-query';
import { useEnqueueSnackbar, useLocationParams } from 'hooks';
import { useIsFetching } from '@tanstack/react-query';
import { PartnershipCreationMode, PartnershipIdentityToShow } from '../types';
import facebookValidator, {
  type FacebookFormData,
} from '../validation/facebookValidator';
import scalingEgValidator, {
  type ScalingEgFormData,
} from '../validation/scalingEgValidator';
import { buildCampaignFetchParams } from '../utils/campaignFetchParams';
import { SINGLE_AD_TYPE } from '../constants';
import { useMe } from 'features/login';
import { useGlobalSettings } from 'providers';
import AdSetBudgetCounter from './AdSetBudgetCounter';
import type { AppCode } from '../../../constants';
import { APP_LIST, MarketingProcessCode, Platform } from '../../../constants';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';
import { DEFAULT_HYPOTHESIS_CREATIVES_VIEW_STATE } from '../types/hypothesis';
import { Box } from '@mui/system';
import { buildFacebookAdSubmitContext } from '../utils/facebookAdSubmitContext';
import {
  buildCreateAdFromPacksPayload,
  buildCreatePartnershipAdsPayload,
} from '../utils/facebookAdSubmitPayload';
import { buildCreateScalingEgAdsPayload } from '../utils/scalingEgAdSubmitPayload';
import {
  isCreativesFormatCompatible,
  isScalingEgProcess,
} from '../utils/marketingProcess';
import { canNavigateToStep } from '../utils/canNavigateToStep';
import { withClearedAdType } from '../utils/clearFormAdType';
import { AdSetAdsCounter } from 'features/facebook';
import useStepRevalidation from '../hooks/useStepRevalidation';
import useStepHistorySync from '../hooks/useStepHistorySync';
import useClearLocationParamsOnJobResult from '../hooks/useClearLocationParamsOnJobResult';
import type { PartnershipAdSettingFormValue } from './PartnershipSection';
import { buildPacksFromCreativesByPack } from '../utils/buildPacksFromCreativesByPack';
import { resolveHypothesisCreativesViewState } from '../utils/savedCreativeFilters';
import {
  alignAdSettingsSlots,
  normalizeAdSettingsByIndex,
} from '../validation/adSettingsValidation';

type FormData = FacebookFormData;

const facebookFormResolver: Resolver<FormData> = async (
  values,
  context,
  options
) => {
  if (isScalingEgProcess(values.marketingProcess)) {
    const scalingEgValues: ScalingEgFormData = {
      ...values,
      marketingProcess: 'scaling_eg',
    };

    return scalingEgValidator.resolver(
      scalingEgValues,
      context,
      options as Parameters<typeof scalingEgValidator.resolver>[2]
    );
  }

  return facebookValidator.resolver(values, context, options);
};

const FacebookForm = () => {
  const { data: me } = useMe();
  const { selectedApp } = useGlobalSettings();
  const {
    marketingProcess: marketingProcessParam,
    app: appParam,
    adType: adTypeParam,
    platform: platformParam,
    adAccounts: adAccountsParam,
    visualTouchUps: visualTouchUpsParam,
    creativesByPack: creativesByPackParam,
    flatCreatives: flatCreativesParam,
    creativesFormat: creativesFormatParam,
    callToAction: callToActionParam,
    conversionDomain: conversionDomainParam,
    hotjar: hotjarParam,
    partnershipAdsEnabled: partnershipAdsEnabledParam,
    partnershipIdentityToShow: partnershipIdentityToShowParam,
    partnershipIsPartnerIdentityFirst: partnershipIsPartnerIdentityFirstParam,
    partnershipCreationMode: partnershipCreationModeParam,
    partners: partnersParam,
    packAdSettings: packAdSettingsParam,
    partnershipAdSettingsFields: partnershipAdSettingsFieldsParam,
    filterCampaignsByName: filterCampaignsByNameParam,
    filterAdSetsByName: filterAdSetsByNameParam,
    filterCreativesByName: filterCreativesByNameParam,
    hypothesisSearch: hypothesisSearchParam,
    hypothesisUploaded: hypothesisUploadedParam,
    hypothesisNames: hypothesisNamesParam,
    hypothesisFlow: hypothesisFlowParam,
    hypothesisCustomer: hypothesisCustomerParam,
    hypothesisTechnology: hypothesisTechnologyParam,
    hypothesisTpcBefore: hypothesisTpcBeforeParam,
    hypothesisStatus: hypothesisStatusParam,
    selectedCampaigns: selectedCampaignsParam,
    selectedAdSets: selectedAdSetsParam,
    step: stepParam,
    setStep: setStepParam,
    setApp: setAppParam,
    setPlatform: setPlatformParam,
    setAdAccounts: setAdAccountsParam,
    setSelectedCampaigns: setSelectedCampaignsParam,
    setSelectedAdSets: setSelectedAdSetsParam,
    setFilterCampaignsByName: setFilterCampaignsByNameParam,
    setFilterAdSetsByName: setFilterAdSetsByNameParam,
    setFilterCreativesByName: setFilterCreativesByNameParam,
    setCreativesByPack: setCreativesByPackParam,
    setFlatCreatives: setFlatCreativesParam,
    setHypothesisSearch: setHypothesisSearchParam,
    setHypothesisUploaded: setHypothesisUploadedParam,
    setHypothesisNames: setHypothesisNamesParam,
    setHypothesisFlow: setHypothesisFlowParam,
    setHypothesisCustomer: setHypothesisCustomerParam,
    setHypothesisTechnology: setHypothesisTechnologyParam,
    setHypothesisTpcBefore: setHypothesisTpcBeforeParam,
    setHypothesisStatus: setHypothesisStatusParam,
    setCallToAction: setCallToActionParam,
    setConversionDomain: setConversionDomainParam,
    setHotjar: setHotjarParam,
    setPartnershipAdsEnabled: setPartnershipAdsEnabledParam,
    setPartnershipIdentityToShow: setPartnershipIdentityToShowParam,
    setPartnershipIsPartnerIdentityFirst:
      setPartnershipIsPartnerIdentityFirstParam,
    setPartnershipCreationMode: setPartnershipCreationModeParam,
    setPartners: setPartnersParam,
    setPackAdSettings: setPackAdSettingsParam,
    setPartnershipAdSettingsFields: setPartnershipAdSettingsFieldsParam,
    resetLocationParamsToStart,
  } = useLocationParams();
  const { onShowErrorAlert } = useEnqueueSnackbar();
  const isHypothesisTestingParam =
    marketingProcessParam === MarketingProcessCode.HYPOTHESIS_TESTING;
  const isScalingEgParam = isScalingEgProcess(marketingProcessParam);
  const isScalingParam = marketingProcessParam === MarketingProcessCode.SCALING;
  const creativesCompatible = isCreativesFormatCompatible(
    marketingProcessParam,
    creativesFormatParam
  );
  const hasUrlCreatives =
    creativesCompatible &&
    (isScalingEgParam
      ? flatCreativesParam.length > 0
      : creativesByPackParam.some((group) => group.creatives.length > 0));
  const resolvedAdType = isHypothesisTestingParam
    ? SINGLE_AD_TYPE
    : (adTypeParam ?? '');

  const getDefaultPartnershipAdSetting = useCallback(
    (): PartnershipAdSettingFormValue => ({
      partnershipId: '',
      partnershipTag: '',
      partnershipPartnerPageId: '',
      partnershipAppPageId: '',
      partnershipPartnerInstagramId: null,
    }),
    []
  );

  const getDefaultValues = useMemo(() => {
    const availableApps = APP_LIST.map((a) => String(a.code));
    const matchedProjects = me?.apps.filter((myApp) =>
      availableApps.includes(myApp)
    );
    // Restores a deep-linked/shared selection for step reachability. Checked
    // creatives are applied only after DeepLinkInitializer validates each id.
    const packsFromUrl = creativesCompatible
      ? buildPacksFromCreativesByPack(
          creativesByPackParam,
          resolvedAdType,
          visualTouchUpsParam
        )
      : [];

    return {
      currentStep: 0,
      app:
        appParam ??
        selectedApp ??
        (matchedProjects ? matchedProjects[0] : undefined),
      platform: platformParam,
      marketingProcess: marketingProcessParam,
      adType: resolvedAdType,
      adAccounts: adAccountsParam,
      selectedCampaigns: [],
      selectedCampaignsOnly: false,
      selectedCampaignsBudgets: {},
      selectedAdSets: [],
      selectedAdSetsBudgets: {},
      selectedAdSetsDuplicatesCount: {},
      selectedAdSetsStartTimes: {},
      selectedAdSetsFlows: {},
      selectedCreatives: !creativesCompatible
        ? []
        : isScalingEgParam
          ? flatCreativesParam
          : isHypothesisTestingParam
            ? []
            : (packsFromUrl[0]?.creatives ?? []),
      // Hypothesis testing builds its packs from selected creatives, so it starts with none
      // unless a URL deep-link already restores some.
      packsWithCreatives: packsFromUrl.length
        ? packsFromUrl.map((pack, index) =>
            index === 0
              ? {
                  ...pack,
                  filterCreativeByName: [...filterCreativesByNameParam],
                }
              : pack,
          )
        : isHypothesisTestingParam
          ? []
          : [
              {
                tag: '',
                adType: resolvedAdType,
                creatives: [],
                filterCreativeByName: [...filterCreativesByNameParam],
                visual_touch_ups: visualTouchUpsParam,
              },
            ],
      activePacksWithCreatives: packsFromUrl.length
        ? 0
        : isHypothesisTestingParam
          ? null
          : 0,
      packAdSettings: packAdSettingsParam.length
        ? normalizeAdSettingsByIndex(packAdSettingsParam)
        : [{}],
      partnershipAdSettingsFields: alignAdSettingsSlots(
        partnershipAdSettingsFieldsParam,
        Math.max(partnersParam.length, 1)
      ),
      selectedConversionDomain: conversionDomainParam ?? '',
      adLabelSuffix: '',
      callToActions: callToActionParam ?? 'LEARN_MORE',
      hotjar: hotjarParam,
      partnershipAdsEnabled: partnershipAdsEnabledParam,
      partnershipIsPartnerIdentityFirst:
        partnershipIsPartnerIdentityFirstParam ??
        (partnershipAdsEnabledParam ? true : undefined),
      partnershipIdentityToShow:
        partnershipIdentityToShowParam ??
        (partnershipAdsEnabledParam
          ? PartnershipIdentityToShow.BOTH
          : undefined),
      partnershipCreationMode:
        partnershipCreationModeParam ??
        (partnershipAdsEnabledParam
          ? PartnershipCreationMode.PARTNERSHIP_ONLY
          : undefined),
      partnershipAdSettings: partnersParam.length
        ? partnersParam.map((partnershipId) => ({
            ...getDefaultPartnershipAdSetting(),
            partnershipId,
          }))
        : [getDefaultPartnershipAdSetting()],
      filterCampaignsByName: filterCampaignsByNameParam,
      filterAdSetsByName: filterAdSetsByNameParam,
      filterCreativesByName: filterCreativesByNameParam,
      selectedCreativesOnly: false,
      activePackTag: '',
      // Restores a deep-linked/shared filter selection for the Hypothesis
      // Testing "Choose Creatives" filter bar.
      testCreativesViewState: {
        ...DEFAULT_HYPOTHESIS_CREATIVES_VIEW_STATE,
        search: hypothesisSearchParam,
        hypothesis: hypothesisNamesParam,
        customer: hypothesisCustomerParam,
        flow: hypothesisFlowParam,
        technology: hypothesisTechnologyParam,
        tpcFinishDate: hypothesisTpcBeforeParam,
        status: hypothesisStatusParam,
        uploaded: hypothesisUploadedParam,
      },
    };
  }, [
    me,
    selectedApp,
    appParam,
    marketingProcessParam,
    isHypothesisTestingParam,
    isScalingEgParam,
    creativesCompatible,
    resolvedAdType,
    platformParam,
    adAccountsParam,
    visualTouchUpsParam,
    creativesByPackParam,
    flatCreativesParam,
    filterCampaignsByNameParam,
    filterAdSetsByNameParam,
    filterCreativesByNameParam,
    hypothesisSearchParam,
    hypothesisUploadedParam,
    hypothesisNamesParam,
    hypothesisFlowParam,
    hypothesisCustomerParam,
    hypothesisTechnologyParam,
    hypothesisTpcBeforeParam,
    hypothesisStatusParam,
    callToActionParam,
    conversionDomainParam,
    hotjarParam,
    partnershipAdsEnabledParam,
    partnershipIdentityToShowParam,
    partnershipIsPartnerIdentityFirstParam,
    partnershipCreationModeParam,
    partnersParam,
    packAdSettingsParam,
    partnershipAdSettingsFieldsParam,
    getDefaultPartnershipAdSetting,
  ]);

  const initialSteps = useMemo<StepType[]>(() => {
    const steps: StepType[] = [
      { title: 'Creatives', component: CreativesForm },
      { title: 'Ad Accounts & Platform', component: AdAccountsForm },
      { title: 'Campaigns', component: CampaignsForm },
      { title: 'Ad Sets', component: AdSetsForm },
      { title: 'Ad', component: AdForm },
    ];

    // Read-only launch summary — Scaling and Scaling EG only for now; Testing,
    // Hypothesis Testing, and Inbox keep the current 5-step flow.
    if (isScalingParam || isScalingEgParam) {
      steps.push({ title: 'Preview', component: LaunchPreviewForm });
    }

    return steps;
  }, [isScalingParam, isScalingEgParam]);

  // Validate URL-restored data synchronously to find the highest reachable
  // step. For each step s below the target we check whether step s can be
  // *passed*:
  //   • Steps 0–1: all required fields are present in getDefaultValues
  //     (restored from URL synchronously), so the Zod schema is the source
  //     of truth.
  //   • Steps 2–3: selectedCampaigns / selectedAdSets are loaded
  //     asynchronously (they start as [] in getDefaultValues), so the schema
  //     would always report them invalid. Check URL-param presence instead.
  //   • Step 4: can't be pre-validated (needs async ad-settings data); if
  //     all earlier steps are satisfied we allow the target unconditionally.
  const initialStep = useMemo(() => {
    const target = Math.min(stepParam, initialSteps.length - 1);
    if (target <= 0 || !creativesCompatible) return 0;

    const validatorSchema = isScalingEgProcess(
      String(getDefaultValues.marketingProcess ?? '')
    )
      ? scalingEgValidator.schema
      : facebookValidator.schema;

    for (let s = 0; s < target; s++) {
      if (s <= 1) {
        const { success } = validatorSchema.safeParse({
          ...getDefaultValues,
          currentStep: s,
        });
        if (!success) return s;
      } else if (s === 2) {
        if (!selectedCampaignsParam.length) return s;
      } else if (s === 3) {
        if (!selectedAdSetsParam.length) return s;
      }
    }
    return target;
  }, [
    stepParam,
    initialSteps.length,
    getDefaultValues,
    selectedCampaignsParam,
    selectedAdSetsParam,
    creativesCompatible,
  ]);

  const {
    currentStep,
    resetSteps,
    prevStep,
    nextStep,
    goToStep,
    StepComponent,
  } = useStepper({ initialSteps, defaultStep: initialStep });

  // When deep-linking to a step > 0, or when the URL has creatives to verify,
  // show an initializer that checks each id before marking it selected.
  // Dismissed by the initializer itself via the callbacks.
  const [isInitializing, setIsInitializing] = useState(
    initialStep > 0 || !creativesCompatible || hasUrlCreatives
  );
  const [initStepStatuses, setInitStepStatuses] = useState<StepInitStatus[]>(
    []
  );

  const handleStatusChange = useCallback((statuses: StepInitStatus[]) => {
    setInitStepStatuses(statuses);
  }, []);

  // Build steps array enriched with per-step init status for the stepper header.
  const stepsWithStatus = useMemo<StepType[]>(
    () =>
      isInitializing
        ? initialSteps.map((step, i) => ({
            ...step,
            initStatus: initStepStatuses[i],
          }))
        : initialSteps,
    [isInitializing, initialSteps, initStepStatuses]
  );

  // While initializing, point the stepper at the step currently being
  // checked so the "active" highlight tracks the verification progress.
  const stepperActiveStep = useMemo(() => {
    if (!isInitializing) return currentStep;
    const loadingIdx = initStepStatuses.findIndex((s) => s === 'loading');
    if (loadingIdx >= 0) return loadingIdx;
    const failedIdx = initStepStatuses.findIndex((s) => s === 'failed');
    if (failedIdx >= 0) return failedIdx;
    return initialStep;
  }, [isInitializing, initStepStatuses, currentStep, initialStep]);

  const adSettingsIsFetching = useIsFetching({
    queryKey: queryKeys.adSettings,
  });
  const partnershipAdSettingsIsFetching = useIsFetching({
    queryKey: queryKeys.partnershipAdSettings,
  });

  const formDefaultValues = useMemo(
    () => ({
      ...getDefaultValues,
      selectedCreatives: [],
      packsWithCreatives: getDefaultValues.packsWithCreatives.map((pack) => ({
        ...pack,
        creatives: [],
      })),
    }),
    [getDefaultValues]
  );

  const formMethods = useForm<FormData>({
    resolver: facebookFormResolver,
    defaultValues: formDefaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  const {
    watch,
    getValues,
    setValue,
    trigger,
    reset: resetForm,
    handleSubmit,
    formState: { isValid, errors },
  } = formMethods;
  const selectedFormApp = watch('app');
  const revalidateForStep = useStepRevalidation({ setValue, trigger });

  const handleInitComplete = useCallback(() => {
    setIsInitializing(false);
    void revalidateForStep(currentStep);
  }, [currentStep, revalidateForStep]);

  // Switching marketing process starts from a clean slate — step 0 and no
  // downstream selections in the form or URL.
  const handleMarketingProcessChange = useCallback(() => {
    setIsInitializing(false);
    goToStep(0);
    setStepParam(0);

    setAdAccountsParam([]);
    setSelectedCampaignsParam([]);
    setSelectedAdSetsParam([]);
    setFilterCampaignsByNameParam([]);
    setFilterAdSetsByNameParam([]);
    setFilterCreativesByNameParam([]);
    setCreativesByPackParam([]);
    setFlatCreativesParam([]);
    setPlatformParam(Platform.WEB);
    setHypothesisSearchParam('');
    setHypothesisUploadedParam(
      DEFAULT_HYPOTHESIS_CREATIVES_VIEW_STATE.uploaded
    );
    setHypothesisNamesParam([]);
    setHypothesisFlowParam([]);
    setHypothesisCustomerParam(null);
    setHypothesisTechnologyParam([]);
    setHypothesisTpcBeforeParam(null);
    setHypothesisStatusParam(null);
    setCallToActionParam(undefined);
    setConversionDomainParam(undefined);
    setHotjarParam(false);
    setPartnershipAdsEnabledParam(false);
    setPartnershipIdentityToShowParam(undefined);
    setPartnershipIsPartnerIdentityFirstParam(undefined);
    setPartnershipCreationModeParam(undefined);
    setPartnersParam([]);
    setPackAdSettingsParam([]);
    setPartnershipAdSettingsFieldsParam([]);

    const app = getValues('app') as string | undefined;

    setValue('adAccounts', [], { shouldValidate: true });
    setValue('platform', Platform.WEB, { shouldValidate: true });
    setValue('currentStep', 0, { shouldValidate: true });
    setValue('filterCampaignsByName', [], { shouldValidate: true });
    setValue('filterAdSetsByName', [], { shouldValidate: true });
    setValue('selectedCampaignsOnly', false, { shouldValidate: true });
    setValue('selectedCampaignsBudgets', {}, { shouldValidate: true });
    setValue('selectedAdSetsBudgets', {}, { shouldValidate: true });
    setValue('selectedAdSetsDuplicatesCount', {}, { shouldValidate: true });
    setValue('selectedAdSetsStartTimes', {}, { shouldValidate: true });
    setValue('selectedAdSetsFlows', {}, { shouldValidate: true });
    setValue('packAdSettings', [{}], { shouldValidate: true });
    setValue('partnershipAdSettingsFields', [{}], { shouldValidate: true });
    setValue('selectedConversionDomain', '', { shouldValidate: true });
    setValue('adLabelSuffix', '', { shouldValidate: true });
    setValue('callToActions', 'LEARN_MORE', { shouldValidate: true });
    setValue('hotjar', false, { shouldValidate: true });
    setValue('partnershipAdsEnabled', false, { shouldValidate: true });
    setValue('partnershipIsPartnerIdentityFirst', undefined, {
      shouldValidate: true,
    });
    setValue('partnershipIdentityToShow', undefined, {
      shouldValidate: true,
    });
    setValue('partnershipCreationMode', undefined, {
      shouldValidate: true,
    });
    setValue('partnershipAdSettings', [getDefaultPartnershipAdSetting()], {
      shouldValidate: true,
    });
    setValue('filterCreativesByName', [], { shouldValidate: true });
    setValue('selectedCreativesOnly', false, { shouldValidate: true });
    setValue('activePackTag', '', { shouldValidate: true });
    setValue(
      'testCreativesViewState',
      resolveHypothesisCreativesViewState(app),
      {
        shouldValidate: true,
      }
    );

    void trigger();
  }, [
    getDefaultPartnershipAdSetting,
    getValues,
    goToStep,
    setAdAccountsParam,
    setCreativesByPackParam,
    setFilterAdSetsByNameParam,
    setFilterCampaignsByNameParam,
    setFilterCreativesByNameParam,
    setFlatCreativesParam,
    setHypothesisCustomerParam,
    setHypothesisFlowParam,
    setHypothesisNamesParam,
    setHypothesisSearchParam,
    setHypothesisStatusParam,
    setHypothesisTechnologyParam,
    setHypothesisTpcBeforeParam,
    setHypothesisUploadedParam,
    setCallToActionParam,
    setConversionDomainParam,
    setHotjarParam,
    setPartnershipAdsEnabledParam,
    setPartnershipIdentityToShowParam,
    setPartnershipIsPartnerIdentityFirstParam,
    setPartnershipCreationModeParam,
    setPartnersParam,
    setPackAdSettingsParam,
    setPartnershipAdSettingsFieldsParam,
    setPlatformParam,
    setSelectedAdSetsParam,
    setSelectedCampaignsParam,
    setStepParam,
    setValue,
    trigger,
  ]);

  // Mirrors the resolved form app to the URL so reloads/deep-links always
  // carry ?app= even when the param was absent on first load.
  useEffect(() => {
    if (!selectedFormApp) return;

    const nextApp = String(selectedFormApp) as AppCode;
    if (appParam === nextApp) return;
    if (!APP_LIST.some(({ code }) => code === nextApp)) return;

    setAppParam(nextApp);
  }, [selectedFormApp, appParam, setAppParam]);

  useEffect(() => {
    setValue('currentStep', currentStep, { shouldValidate: true });
  }, [currentStep, setValue]);

  const {
    data: jobStatusResponse,
    runJubStatusCheck,
    isLoading: isJobStatusChecking,
    isFinished: isJobStatusFinished,
    clearJobStatus,
  } = useJobStatusCheck();

  useClearLocationParamsOnJobResult(isJobStatusFinished);

  // Applies browser Back / Forward to the stepper (Next / Back push a history
  // entry per step, see handleNextStep / handlePrevStep).
  useStepHistorySync({
    stepParam,
    currentStep,
    stepCount: initialSteps.length,
    goToStep,
    revalidateForStep,
    enabled: !isJobStatusFinished,
  });

  // 'Ad' is always index 4 — Preview may be appended after it, so this can't
  // be derived from initialSteps.length like isDisabledNext/titleNext are.
  const AD_STEP_INDEX = 4;
  const isAdStep = currentStep === AD_STEP_INDEX;

  useEffect(() => {
    if (!isAdStep) {
      return;
    }

    if (adSettingsIsFetching || partnershipAdSettingsIsFetching) {
      return;
    }

    void trigger();
  }, [
    adSettingsIsFetching,
    isAdStep,
    partnershipAdSettingsIsFetching,
    trigger,
  ]);

  useEffect(() => {
    const stepTitle = initialSteps[currentStep]?.title;
    if (!stepTitle) {
      return;
    }

    datadogActions.trackDatadogAction(DATADOG_EVENT.facebookFormStepChange, {
      stepTitle,
      stepIndex: currentStep,
    });
  }, [currentStep, initialSteps]);

  const handleInitReset = useCallback(() => {
    setIsInitializing(false);
    setInitStepStatuses([]);
    goToStep(0);
    resetForm(
      withClearedAdType(
        {
          ...formDefaultValues,
          currentStep: 0,
        },
        isHypothesisTestingParam
      )
    );
    clearJobStatus();
  }, [
    clearJobStatus,
    formDefaultValues,
    goToStep,
    isHypothesisTestingParam,
    resetForm,
  ]);

  useEffect(() => {
    if (!selectedApp || String(selectedFormApp ?? '') === String(selectedApp)) {
      return;
    }

    // URL `app` already matches the form: the sidebar selector catching up
    // after a deep link is not a user app switch, so keep URL selections.
    if (
      appParam &&
      String(appParam) === String(selectedFormApp) &&
      String(appParam) !== String(selectedApp)
    ) {
      return;
    }

    // URL `app` already matches the form: the sidebar selector catching up
    // after a deep link is not a user app switch, so keep URL selections.
    if (
      appParam &&
      String(appParam) === String(selectedFormApp) &&
      String(appParam) !== String(selectedApp)
    ) {
      return;
    }

    resetForm({
      ...formDefaultValues,
      app: selectedApp,
      currentStep: 0,
    });
    resetSteps();
    setStepParam(initialStep);
    clearJobStatus();
  }, [
    clearJobStatus,
    formDefaultValues,
    initialStep,
    resetForm,
    resetSteps,
    setStepParam,
    selectedApp,
    selectedFormApp,
    appParam,
  ]);

  const createAdsMutationConfig = useMemo(
    () => ({
      onSuccess: (r: CreateFacebookAdResponse) => {
        datadogActions.trackDatadogAction(
          DATADOG_EVENT.createFacebookAdSuccess,
          {
            jobId: r.job_id,
          }
        );
        runJubStatusCheck(r.job_id);
      },
      onError: (err: Error & { cause?: unknown }) => {
        datadogActions.trackDatadogError(err, {
          source: 'create_facebook_ad_error',
        });
        if (err.cause) {
          onShowErrorAlert(
            (err.cause as { details?: string })?.details ?? 'Error occur',
            { delayAlert: 3500 }
          );
          console.error(err.cause);
        }
      },
    }),
    [onShowErrorAlert, runJubStatusCheck]
  );

  const createFacebookAd = useCreateFacebookAd({
    config: createAdsMutationConfig,
  });

  const createPartnershipAds = useCreatePartnershipAds({
    config: createAdsMutationConfig,
  });

  const createScalingEgAds = useCreateScalingEgAds({
    config: createAdsMutationConfig,
  });

  const onSubmit = useCallback(
    (data: FormData) => {
      try {
        if (!isValid) return;

        const submitContext = buildFacebookAdSubmitContext(queryClient);

        if (isScalingEgProcess(data.marketingProcess)) {
          const payload: CreateScalingEgAdsParams =
            buildCreateScalingEgAdsPayload(data, submitContext);
          createScalingEgAds.mutate(payload);
        } else {
          const allPacksAreSingleAd = data.packsWithCreatives.every(
            (pack) => pack.adType === SINGLE_AD_TYPE
          );
          const isPartnershipAdsFlow =
            data.partnershipAdsEnabled && allPacksAreSingleAd;

          if (isPartnershipAdsFlow) {
            const payload: CreatePartnershipAdsParams =
              buildCreatePartnershipAdsPayload(data, submitContext);
            createPartnershipAds.mutate(payload);
          } else {
            const payload: CreateFacebookAdFromPacksParams =
              buildCreateAdFromPacksPayload(data, submitContext);
            createFacebookAd.mutate(payload);
          }
        }

        datadogActions.trackDatadogAction(
          DATADOG_EVENT.facebookFormSubmitAttempt,
          {
            app: data.app,
            platform: data.platform,
            marketingProcess: data.marketingProcess,
            adAccountsCount: data.adAccounts?.length ?? 0,
            adSetsCount: data.selectedAdSets?.length ?? 0,
            creativePacksCount: data.packsWithCreatives?.length ?? 0,
            partnershipAdsEnabled: data.partnershipAdsEnabled,
          }
        );
      } catch (error) {
        console.error('Error during Facebook ad creation:', error);
        datadogActions.trackDatadogError(error as Error, {
          source: 'facebook_form_submit_try_catch',
        });
        onShowErrorAlert('Error during Facebook ad creation', {
          delayAlert: 3500,
        });
      }
    },
    [
      isValid,
      onShowErrorAlert,
      createFacebookAd,
      createPartnershipAds,
      createScalingEgAds,
    ]
  );

  const adAccounts: string[] | undefined = formMethods.watch('adAccounts');
  const marketingProcess: string = formMethods.watch('marketingProcess');
  const isMarketingProcessHypothesisTesting =
    marketingProcess === 'hypothesis_testing';
  const isMarketingProcessScalingEg = isScalingEgProcess(marketingProcess);

  useCampaigns({
    params: buildCampaignFetchParams(marketingProcess, adAccounts ?? []),
    config: {
      enabled: !!adAccounts?.length && !!marketingProcess,
    },
  });

  const handleNextStep = useCallback(async () => {
    const valid = await trigger();
    if (
      !valid ||
      createFacebookAd.isPending ||
      createPartnershipAds.isPending ||
      createScalingEgAds.isPending
    )
      return;

    if (currentStep === initialSteps.length - 1) {
      void handleSubmit(onSubmit)();
    } else {
      nextStep();
      setStepParam(currentStep + 1, 'pushIn');
      void revalidateForStep(currentStep + 1);
    }
  }, [
    nextStep,
    trigger,
    handleSubmit,
    onSubmit,
    initialSteps,
    currentStep,
    revalidateForStep,
    setStepParam,
    createFacebookAd.isPending,
    createPartnershipAds.isPending,
    createScalingEgAds.isPending,
  ]);

  const handlePrevStep = useCallback(() => {
    const target = Math.max(currentStep - 1, 0);
    prevStep();
    setStepParam(target, 'pushIn');
    void revalidateForStep(target);
  }, [prevStep, revalidateForStep, setStepParam, currentStep]);

  const handleRestart = useCallback(() => {
    datadogActions.trackDatadogAction(DATADOG_EVENT.facebookFormRestart);
    resetLocationParamsToStart();
    resetForm(
      withClearedAdType(
        {
          ...formDefaultValues,
          currentStep: 0,
        },
        isHypothesisTestingParam
      )
    );
    resetSteps();
    setStepParam(initialStep);
    clearJobStatus();
  }, [
    resetLocationParamsToStart,
    resetForm,
    formDefaultValues,
    isHypothesisTestingParam,
    resetSteps,
    setStepParam,
    initialStep,
    clearJobStatus,
  ]);

  const handleLaunchMoreAds = useCallback(() => {
    window.open('/facebook', '_blank', 'noopener');
  }, []);

  const isCreateAdsPending =
    createFacebookAd.isPending ||
    createPartnershipAds.isPending ||
    createScalingEgAds.isPending;
  const isCreateAdsSuccess =
    createFacebookAd.isSuccess ||
    createPartnershipAds.isSuccess ||
    createScalingEgAds.isSuccess;
  const isNavigationLocked =
    isCreateAdsPending || !!isJobStatusChecking || isInitializing;
  const isDisabledNext =
    !!adSettingsIsFetching ||
    !!partnershipAdSettingsIsFetching ||
    isNavigationLocked ||
    !isValid;

  const handleStepClick = useCallback(
    (index: number) => {
      if (isNavigationLocked || index === currentStep) return;

      const values = getValues();
      const schema = isScalingEgProcess(values.marketingProcess)
        ? scalingEgValidator.schema
        : facebookValidator.schema;
      if (!canNavigateToStep(index, currentStep, values, schema)) return;

      goToStep(index);
      setStepParam(index, 'pushIn');
      void revalidateForStep(index);
    },
    [
      currentStep,
      getValues,
      goToStep,
      isNavigationLocked,
      revalidateForStep,
      setStepParam,
    ]
  );

  const formValues = watch();

  const stepperSteps = useMemo<StepType[]>(() => {
    if (isNavigationLocked || isJobStatusFinished) {
      return stepsWithStatus.map((step) => ({ ...step, clickable: false }));
    }

    const schema = isScalingEgProcess(formValues.marketingProcess)
      ? scalingEgValidator.schema
      : facebookValidator.schema;

    return stepsWithStatus.map((step, index) => ({
      ...step,
      clickable: canNavigateToStep(index, currentStep, formValues, schema),
    }));
  }, [
    currentStep,
    formValues,
    isJobStatusFinished,
    isNavigationLocked,
    stepsWithStatus,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[FacebookForm]', {
        isValid,
        adSettingsIsFetching: !!adSettingsIsFetching,
        partnershipAdSettingsIsFetching: !!partnershipAdSettingsIsFetching,
        isNavigationLocked,
        validationErrors: errors,
      });
    }
  }, [
    isValid,
    errors,
    adSettingsIsFetching,
    partnershipAdSettingsIsFetching,
    isNavigationLocked,
  ]);

  return (
    <FormProvider {...formMethods}>
      <StepperPaper
        title={'Facebook Ads Automation'}
        steps={stepperSteps}
        activeStep={isJobStatusFinished ? -1 : stepperActiveStep}
        onStepClick={handleStepClick}
        topTabs={
          <MarketingProcessTabs
            onProcessChange={handleMarketingProcessChange}
          />
        }
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            width: '100%',
            alignItems: 'start',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              width: '100%',
              alignItems: 'start',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                width: '100%',
                height: '100%',
                alignItems: 'start',
                justifyContent: 'start',
                flexDirection: 'column',
                position: 'absolute',
              }}
            >
              {isJobStatusChecking ? (
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    height: '100%',
                    flex: 1,
                  }}
                >
                  <AdCreationProgress onLaunchMoreAds={handleLaunchMoreAds} />
                </Box>
              ) : (
                <Suspense fallback={<CircularProgress />}>
                  {isJobStatusFinished ? (
                    <JobResultTable rows={jobStatusResponse?.result} />
                  ) : isInitializing ? (
                    <DeepLinkInitializer
                      targetStep={initialStep}
                      onComplete={handleInitComplete}
                      onReset={handleInitReset}
                      onStatusChange={handleStatusChange}
                    />
                  ) : (
                    <StepComponent />
                  )}
                </Suspense>
              )}
            </Box>
          </Box>
          <FormNavigation
            isJobStatusFinished={isJobStatusFinished}
            onRestart={handleRestart}
            isDisabledRestart={!currentStep || isNavigationLocked}
            onBack={handlePrevStep}
            isDisabledBack={!currentStep || isNavigationLocked}
            onNext={handleNextStep}
            isDisabledNext={isDisabledNext}
            isLoadingNext={isCreateAdsPending}
            titleNext={
              currentStep === initialSteps.length - 1 ? 'Create Ads' : 'Next'
            }
            childrenOnTheRightSide={
              <>
                <>
                  <AdSetAdsCounter />
                  <AdSetBudgetCounter />
                </>
                {currentStep === 0 && isMarketingProcessHypothesisTesting ? (
                  <>
                    <SplitInPacksButton />
                    <CreateMixedPackButton />
                    <AddHypothesisPackButton />
                  </>
                ) : null}
                {currentStep === 0 &&
                !isMarketingProcessHypothesisTesting &&
                !isMarketingProcessScalingEg ? (
                  <AddPackButton />
                ) : null}
              </>
            }
          />
        </Box>
      </StepperPaper>
      {!jobStatusResponse?.result && (
        <ConfirmationDialog
          isUnblock={isCreateAdsSuccess && isJobStatusFinished}
        />
      )}
    </FormProvider>
  );
};
export default FacebookForm;
